import { notify } from "../components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
} from "../lib/storeManager";
import { hidenMessageAPI } from "../utils/constant/hidenMessageAPI";
import axios from "axios";

export const axiosConfig = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export const zulipAxiosConfig = axios.create({
  baseURL: import.meta.env.VITE_CITYCOLLAB_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

declare module "axios" {
  export interface AxiosRequestConfig {
    notifyMessage?: boolean; // custom flag for controlling notifications
  }
  export interface InternalAxiosRequestConfig {
    notifyMessage?: boolean;
  }
}

const setupZulipInterceptors = (instance: any) => {
  instance.interceptors.request.use(
    function configHeader(config: any) {
      const authToken = localStorage.getItem("zulipToken");
      if (authToken) {
        config.headers = {
          ...config.headers,
          Authorization: authToken,
        };
      }
      return config;
    },
    function configError(error: any) {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response: any) => {
      if (response?.status === 200 || response?.status === 201) {
        return response.data;
      }
      return null;
    },
    (error: any) => {
      if (error?.response?.data) {
        const message = error.response.data;
        if (message?.message) {
          const errorMessages = message.message;
          if (Array.isArray(errorMessages)) {
            errorMessages.forEach((msg) => console.error(msg));
          } else {
            console.error(errorMessages);
          }
        }
      }
      return null;
    }
  );
};

const setupInterceptors = (instance: any) => {
  instance.interceptors.request.use((config: any) => {
    const token = getAccessToken();
    const tenantId = getTenantId();
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Don't add Authorization header for refresh token requests
    if (
      token &&
      config.headers &&
      !config.url?.includes("/auth/token-refresh/")
    ) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (tenantId && config.headers) {
      config.headers["X-Tenant-Id"] = tenantId;
    }
    if (config.headers) {
      config.headers["X-User-Timezone"] = userTimezone;
    }

    return config;
  });

  instance.interceptors.response.use(
    (response: any) => {
      const apiStatus = response.status;
      const notifyMessage = response.config.notifyMessage ?? true;
      if (shouldHideMessage(response.config.url, response.config.baseURL)) {
        return response;
      }
      if (
        (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
        response.data?.success
      ) {
        const message = response.data?.success?.message ?? response.data.message;
        if (message && notifyMessage) {
          notify.success(message);
        }
      }
      return response;
    },
    async (error: any) => {
      const originalRequest = error.config;

      // Check if this API should hide error messages
      if (shouldHideMessage(originalRequest.url, originalRequest.baseURL)) {
        return Promise.reject(error);
      }

      if (
        error.response?.status === 400 ||
        error.response?.status === 404 ||
        error.response?.status === 417
      ) {
        notify.error(
          error.response.data.message ??
          error.response.data.error.message ??
          error.response.data.error
        );
        return Promise.reject(error);
      }
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        getRefreshToken()
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return instance(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await refreshToken();
          const newAccessToken = response.access;
          setLocalAccessToken(newAccessToken);
          if (response.refresh) {
            setLocalRefreshToken(response.refresh);
          }
          instance.defaults.headers.common["Authorization"] =
            `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearLocalStorage();
          window.location.reload();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Check if this API should hide error messages
        if (!shouldHideMessage(originalRequest.url, originalRequest.baseURL)) {
          notify.error(
            error.response?.data?.error?.message ??
            error.response?.data?.message ??
            error.response?.data?.error
          );
        }
        return Promise.reject(error);
      }
    }
  );
};

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper function to check if URL should hide messages
const shouldHideMessage = (
  url: string | undefined,
  baseURL: string | undefined
): boolean => {
  if (!url) {
    return false;
  }

  try {
    // Decode URL to handle encoded characters
    const decodedUrl = decodeURIComponent(url);
    const fullUrl = baseURL ? `${baseURL}${url}` : url;
    const decodedFullUrl = baseURL ? `${baseURL}${decodedUrl}` : decodedUrl;

    return hidenMessageAPI.some(path => {
      try {
        const decodedPath = decodeURIComponent(path);
        return (
          url.includes(path) ||
          decodedUrl.includes(path) ||
          url.includes(decodedPath) ||
          decodedUrl.includes(decodedPath) ||
          fullUrl.includes(path) ||
          decodedFullUrl.includes(path)
        );
      } catch {
        // If path decoding fails, just check with original path
        return (
          url.includes(path) ||
          decodedUrl.includes(path) ||
          fullUrl.includes(path) ||
          decodedFullUrl.includes(path)
        );
      }
    });
  } catch {
    // If URL decoding fails, check with original URL only
    const fullUrl = baseURL ? `${baseURL}${url}` : url;
    return hidenMessageAPI.some(
      path => url.includes(path) || fullUrl.includes(path)
    );
  }
};

const refreshToken = async () => {
  const response = await axios.post(
    `${import.meta.env.VITE_API_BASE_URL}/auth/token-refresh/`,
    new URLSearchParams({
      refresh: getRefreshToken() ?? "",
    }),
    {
      headers: {
        "X-Tenant-Id": getTenantId(),
        "X-User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        "Content-Type": "application/x-www-form-urlencoded",
      },
    }
  );
  return response.data;
};

// Apply interceptors
setupInterceptors(axiosConfig);
setupZulipInterceptors(zulipAxiosConfig);

export default axiosConfig;
